import { useEffect, useState, useCallback } from "react";
import { Search, X, Mail, Phone, Download, Plus, ShieldAlert, Calendar, Video, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ConflictCheckModal from "@/admin/ConflictCheckModal";

const STATUSES = ["Scheduled", "Completed", "Cancelled", "No-show"];
const CHANNELS = ["Phone", "Video", "In person", "WhatsApp"];
const FEES = ["Unpaid", "Paid", "Waived", "N/A"];
const badge = (s) => ({
  Scheduled: "bg-blue-100 text-blue-700", Completed: "bg-emerald-100 text-emerald-700",
  Cancelled: "bg-slate-200 text-slate-600", "No-show": "bg-red-100 text-red-700",
}[s] || "bg-slate-100 text-slate-600");
const feeBadge = (s) => ({ Paid: "bg-emerald-100 text-emerald-700", Unpaid: "bg-amber-100 text-amber-700" }[s] || "bg-slate-100 text-slate-500");
const fmt = (d) => { if (!d) return "Not scheduled"; try { return new Date(d).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); } catch { return d; } };
const EMPTY = { name: "", email: "", phone: "", service_area: "", summary: "", scheduled_at: "", channel: "Video", adviser: "", fee_status: "Unpaid" };

export default function ConsultationsManager() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [staff, setStaff] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState({});
  const [note, setNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [conflict, setConflict] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertForm, setConvertForm] = useState({ create_matter: true, matter_type: "" });

  const load = useCallback(() => {
    const params = {};
    if (status !== "all") params.status = status;
    if (q) params.q = q;
    api.get("/admin/consultations", { params }).then((r) => setItems(r.data)).catch(() => {});
  }, [status, q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {}); }, []);

  const open = (c) => { setActive(c); setDraft({ ...c }); };
  const save = async () => {
    try {
      const payload = (({ scheduled_at, channel, adviser, status, fee_status, fee_amount, pre_notes, outcome, follow_up }) =>
        ({ scheduled_at, channel, adviser, status, fee_status, fee_amount, pre_notes, outcome, follow_up }))(draft);
      const { data } = await api.put(`/admin/consultations/${active.id}`, payload);
      setActive(data); setDraft({ ...data }); load(); toast.success("Consultation saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setStatusQuick = async (s) => {
    const { data } = await api.put(`/admin/consultations/${active.id}`, { status: s });
    setActive(data); setDraft({ ...data }); load();
  };
  const addNote = async () => {
    if (!note.trim()) return;
    const { data } = await api.put(`/admin/consultations/${active.id}`, { note });
    setActive(data); setDraft({ ...data }); setNote(""); toast.success("Note added"); load();
  };
  const submitCreate = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/consultations", form); toast.success("Consultation created"); setCreating(false); setForm(EMPTY); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const exportCsv = () => {
    const rows = [["Scheduled", "Name", "Email", "Area", "Channel", "Adviser", "Fee", "Status"]];
    items.forEach((c) => rows.push([fmt(c.scheduled_at), c.name, c.email, c.service_area || "", c.channel || "", c.adviser || "", c.fee_status || "", c.status]));
    const csv = rows.map((r) => r.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "consultations.csv"; a.click();
  };
  const d = (k, v) => setDraft((p) => ({ ...p, [k]: v }));

  const doConvert = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/admin/consultations/${active.id}/convert-to-client`, convertForm);
      toast.success(data.matter ? `Client created and matter ${data.matter.ref} opened` : "Client record created");
      setConverting(false);
      const { data: fresh } = await api.get(`/admin/consultations/${active.id}`);
      setActive(fresh); setDraft({ ...fresh }); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="consultations-manager">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold text-navy">Consultations</h1>
          <p className="mt-1 text-sm text-slate-500">{items.length} scheduled consultation(s) · booking, outcome and follow-up.</p>
        </div>
        <div className="flex gap-2">
          {can("consultations", "create") && <button onClick={() => { setForm(EMPTY); setCreating(true); }} data-testid="new-consultation-btn" className="btn-gold text-sm"><Plus size={15} /> New consultation</button>}
          <button onClick={exportCsv} className="btn-navy text-sm"><Download size={15} /> Export CSV</button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, area…" className="pl-9" data-testid="consultation-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-slate-300 px-3 py-2 text-sm" data-testid="consultation-status-filter">
          <option value="all">All statuses</option>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="px-5 py-3">Name</th><th className="px-5 py-3 hidden md:table-cell">Scheduled</th><th className="px-5 py-3 hidden lg:table-cell">Channel</th><th className="px-5 py-3 hidden sm:table-cell">Fee</th><th className="px-5 py-3">Status</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((c) => (
              <tr key={c.id} data-testid={`consultation-row-${c.id}`} onClick={() => open(c)} className="cursor-pointer hover:bg-slate-50">
                <td className="px-5 py-3"><div className="font-medium text-navy">{c.name}</div><div className="text-xs text-slate-400">{c.service_area || c.email}</div></td>
                <td className="px-5 py-3 hidden md:table-cell text-xs text-slate-500">{fmt(c.scheduled_at)}</td>
                <td className="px-5 py-3 hidden lg:table-cell text-slate-600">{c.channel || "—"}</td>
                <td className="px-5 py-3 hidden sm:table-cell"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${feeBadge(c.fee_status)}`}>{c.fee_status || "—"}</span></td>
                <td className="px-5 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-medium ${badge(c.status)}`}>{c.status}</span></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-400">No consultations yet. Book one from the Enquiries page.</td></tr>}
          </tbody>
        </table>
      </div>

      {active && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setActive(null)} />
          <div className="relative flex h-full w-full max-w-lg flex-col bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <h2 className="font-serif text-xl font-semibold text-navy">{active.name}</h2>
              <button onClick={() => setActive(null)} className="text-slate-400 hover:text-navy"><X size={20} /></button>
            </div>
            <div className="flex-1 space-y-5 overflow-y-auto p-6">
              <div className="space-y-1.5 text-sm">
                <div className="flex items-center gap-2 text-slate-600"><Mail size={15} className="text-gold" /> {active.email}</div>
                {active.phone && <div className="flex items-center gap-2 text-slate-600"><Phone size={15} className="text-gold" /> {active.phone}</div>}
                {active.summary && <p className="rounded-lg bg-slate-50 p-3 text-slate-700">{active.summary}</p>}
              </div>

              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <button key={s} onClick={() => setStatusQuick(s)} data-testid={`consultation-set-${s.replace(/\W/g, "")}`}
                    disabled={!can("consultations", "edit")}
                    className={`rounded-full px-3 py-1.5 text-xs font-medium disabled:opacity-50 ${draft.status === s ? badge(s) + " ring-2 ring-offset-1 ring-navy/20" : "bg-slate-100 text-slate-500"}`}>{s}</button>
                ))}
              </div>

              <button onClick={() => setConflict(true)} data-testid="consultation-conflict-btn" className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-xs font-medium text-navy hover:bg-slate-50"><ShieldAlert size={14} /> Conflict check</button>

              <fieldset disabled={!can("consultations", "edit")} className="space-y-4 disabled:opacity-70">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div><Label className="flex items-center gap-1 text-xs"><Calendar size={13} /> Date &amp; time</Label><Input type="datetime-local" data-testid="cons-datetime" value={draft.scheduled_at || ""} onChange={(e) => d("scheduled_at", e.target.value)} className="mt-1" /></div>
                  <div><Label className="flex items-center gap-1 text-xs"><Video size={13} /> Channel</Label><select data-testid="cons-channel" value={draft.channel || "Video"} onChange={(e) => d("channel", e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm">{CHANNELS.map((c) => <option key={c}>{c}</option>)}</select></div>
                  <div><Label className="text-xs">Adviser</Label><select data-testid="cons-adviser" value={draft.adviser || ""} onChange={(e) => d("adviser", e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"><option value="">Unassigned</option>{staff.map((s) => <option key={s.email} value={s.email}>{s.name}</option>)}</select></div>
                  <div><Label className="text-xs">Fee status</Label><select data-testid="cons-fee" value={draft.fee_status || "Unpaid"} onChange={(e) => d("fee_status", e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm">{FEES.map((f) => <option key={f}>{f}</option>)}</select></div>
                </div>
                <div><Label className="text-xs">Pre-consultation notes</Label><Textarea data-testid="cons-prenotes" value={draft.pre_notes || ""} onChange={(e) => d("pre_notes", e.target.value)} rows={2} className="mt-1" /></div>
                <div><Label className="text-xs">Adviser outcome</Label><Textarea data-testid="cons-outcome" value={draft.outcome || ""} onChange={(e) => d("outcome", e.target.value)} rows={3} className="mt-1" placeholder="Record the outcome of the consultation…" /></div>
                <div><Label className="text-xs">Follow-up actions</Label><Textarea data-testid="cons-followup" value={draft.follow_up || ""} onChange={(e) => d("follow_up", e.target.value)} rows={2} className="mt-1" /></div>
                <button onClick={save} data-testid="save-consultation-btn" className="btn-gold w-full text-sm">Save consultation</button>
              </fieldset>

              <div className="rounded-lg border border-dashed border-slate-300 p-3 text-center">
                {active.converted_to_client ? (
                  <div className="flex items-center justify-center gap-2 text-xs text-emerald-700"><UserCheck size={15} /> Converted to a client record.</div>
                ) : can("clients", "create") ? (
                  <button onClick={() => { setConvertForm({ create_matter: true, matter_type: active.service_area || "" }); setConverting(true); }}
                    data-testid="convert-to-client-btn" className="btn-navy w-full text-sm"><UserCheck size={15} /> Convert to Client &amp; Matter</button>
                ) : (
                  <span className="text-xs text-slate-400">Converting to a client requires client-creation permission.</span>
                )}
              </div>

              <div>
                <div className="mb-2 text-xs font-semibold uppercase text-slate-400">Internal notes</div>
                {(active.notes || []).map((n, i) => (
                  <div key={i} className="mb-2 rounded-lg border border-slate-100 bg-slate-50 p-2.5 text-xs"><div className="text-slate-700">{n.text}</div><div className="mt-1 text-slate-400">{n.by} · {fmt(n.at)}</div></div>
                ))}
                {can("consultations", "edit") && <><Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note…" rows={2} data-testid="consultation-note-input" />
                <button onClick={addNote} className="btn-navy mt-2 text-xs">Add note</button></>}
              </div>
            </div>
          </div>
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCreating(false)} />
          <div className="relative z-10 max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between"><h2 className="font-serif text-xl font-semibold text-navy">New consultation</h2><button onClick={() => setCreating(false)} className="text-slate-400"><X size={18} /></button></div>
            <form onSubmit={submitCreate} className="space-y-3">
              <div><Label>Name</Label><Input data-testid="create-cons-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input data-testid="create-cons-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1" /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date &amp; time</Label><Input type="datetime-local" data-testid="create-cons-datetime" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className="mt-1" /></div>
                <div><Label>Channel</Label><select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm">{CHANNELS.map((c) => <option key={c}>{c}</option>)}</select></div>
              </div>
              <div><Label>Adviser</Label><select data-testid="create-cons-adviser" value={form.adviser} onChange={(e) => setForm({ ...form, adviser: e.target.value })} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"><option value="">Unassigned</option>{staff.map((s) => <option key={s.email} value={s.email}>{s.name}</option>)}</select></div>
              <button type="submit" data-testid="create-cons-submit" className="btn-gold w-full">Create consultation</button>
            </form>
          </div>
        </div>
      )}

      {conflict && active && (
        <ConflictCheckModal subject={{ name: active.name, email: active.email, phone: active.phone }}
          linkedType="consultation" linkedId={active.id} onClose={() => setConflict(false)} />
      )}

      {converting && active && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConverting(false)} />
          <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between"><h2 className="font-serif text-xl font-semibold text-navy">Convert to Client &amp; Matter</h2><button onClick={() => setConverting(false)} className="text-slate-400"><X size={18} /></button></div>
            <p className="mb-4 text-xs text-slate-500">Creates (or reuses) a client record for <strong>{active.name}</strong> carrying this consultation's details forward. Optionally open a matter at the same time.</p>
            <form onSubmit={doConvert} className="space-y-3">
              <label className="flex items-center gap-2 text-sm text-navy">
                <input type="checkbox" data-testid="convert-create-matter" checked={convertForm.create_matter} onChange={(e) => setConvertForm({ ...convertForm, create_matter: e.target.checked })} className="h-4 w-4 accent-[#B8893B]" />
                Also open a matter
              </label>
              {convertForm.create_matter && (
                <div><Label className="text-xs">Matter type</Label><Input data-testid="convert-matter-type" value={convertForm.matter_type} onChange={(e) => setConvertForm({ ...convertForm, matter_type: e.target.value })} className="mt-1" placeholder="e.g. Spouse & Partner Visa" /></div>
              )}
              <button type="submit" data-testid="confirm-convert-btn" className="btn-gold w-full">Convert now</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
